/**
 * DeleteDataAgentModal Component
 *
 * Confirmation modal for deleting a data agent.
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { useDeleteDataAgent } from '../hooks/useDeleteDataAgent';
import { useAuth } from '../../../contexts/AuthContext';
import type { DataAgent } from '../../../types/data-agent.types';

interface DeleteDataAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  dataAgent: DataAgent;
}

export const DeleteDataAgentModal: React.FC<DeleteDataAgentModalProps> = ({
  isOpen,
  onClose,
  dataAgent,
}) => {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId || '';
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { mutate: deleteDataAgent, isPending } = useDeleteDataAgent(
    tenantId,
    dataAgent.dataAgentId,
    {
      onSuccess: () => {
        onClose();
      },
      onError: (error) => {
        setErrorMessage(error);
      },
    }
  );

  const handleDelete = () => {
    setErrorMessage('');
    deleteDataAgent();
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="sm">
      <Modal.Header>Delete Data Agent</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          <p className="text-sm text-gray-600">
            Are you sure you want to delete the data agent{' '}
            <span className="font-semibold">{dataAgent.dataAgentName}</span>?
          </p>

          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-700">
              This action cannot be undone. All ELT jobs assigned to this agent will need
              to be reassigned before they can execute.
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={isPending}>
              Delete Agent
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
